import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { View } from 'tamagui';

import { ROUTE_PATHNAMES } from '../../routes/routeConstants';
import {
  Home,
  Water,
  HVAC,
  Air,
  Electrical,
  Settings,
  WiFiConnectionSettings,
  LockScreenSettings,
  ServiceContactSettings,
  GeneralSettings,
  Weather,
  SetPinScreen,
  ResetPinScreen,
  WifiConnectScreen,
} from '../../screens';
import { BacklightSettings } from '../../screens/Settings_Backlight';

interface RouteCacheProps {
  maxCacheSize?: number;
}

export const RouteCache: React.FC<RouteCacheProps> = ({ maxCacheSize = 15 }) => {
  const location = useLocation();
  const [cachedComponents] = useState<Map<string, ReactNode>>(() => new Map());

  const routeComponentMap = useMemo(
    () => ({
      [ROUTE_PATHNAMES.dashboard]: <Home />,
      [ROUTE_PATHNAMES.water]: <Water />,
      [ROUTE_PATHNAMES.hvac]: <HVAC />,
      [ROUTE_PATHNAMES.air]: <Air />,
      [ROUTE_PATHNAMES.energy]: <Electrical />,
      [ROUTE_PATHNAMES.settings]: <Settings />,
      [ROUTE_PATHNAMES.wifi]: <WiFiConnectionSettings />,
      [ROUTE_PATHNAMES.lockScreen]: <LockScreenSettings />,
      [ROUTE_PATHNAMES.backlight]: <BacklightSettings />,
      [ROUTE_PATHNAMES.contact]: <ServiceContactSettings />,
      [`${ROUTE_PATHNAMES.settings}/general`]: <GeneralSettings />,
      [ROUTE_PATHNAMES.weather]: <Weather />,
      [ROUTE_PATHNAMES.setPin]: <SetPinScreen />,
      [ROUTE_PATHNAMES.resetPin]: <ResetPinScreen />,
    }),
    [],
  );

  const getCurrentComponent = useCallback(() => {
    const currentPath = location.pathname;

    if (cachedComponents.has(currentPath)) {
      return cachedComponents.get(currentPath);
    }

    let component = routeComponentMap[currentPath];

    if (!component && currentPath.includes('/connect-wifi')) {
      component = <WifiConnectScreen />;
    }

    if (component) {
      cachedComponents.set(currentPath, component);
      if (cachedComponents.size > maxCacheSize) {
        const entries = Array.from(cachedComponents.entries());
        const entriesToRemove = entries.slice(0, cachedComponents.size - maxCacheSize);
        entriesToRemove.forEach(([key]) => cachedComponents.delete(key));
      }
    }

    return component;
  }, [location.pathname, routeComponentMap, cachedComponents, maxCacheSize]);

  const currentComponent = getCurrentComponent();

  return (
    <View flex={1} width="100%" height="100%">
      {Array.from(cachedComponents.entries()).map(([path, component]) => (
        <View
          key={path}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          style={{
            display: path === location.pathname ? 'flex' : 'none',
            zIndex: path === location.pathname ? 1 : 0,
          }}>
          {component}
        </View>
      ))}

      {!cachedComponents.has(location.pathname) && currentComponent && (
        <View flex={1}>{currentComponent}</View>
      )}
    </View>
  );
};

export const useCacheStats = () => {
  const [stats, _setStats] = useState({ size: 0, paths: [] as string[] });
  return stats;
};
